import { FC, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { AudioPlayer, Icon, VideoPlayer } from 'src/components';
import * as S from './styles';

const msgs = defineMessages({
  nextSpeaker: {
    id: 'pronunciation_session_next_speaker',
    defaultMessage: 'Show another speaker',
  },
  selectSpeaker: {
    id: 'pronunciation_session_select_speaker',
    defaultMessage: 'Show speaker {number}',
  },
});

export type SpeakerMediaItem = { type: 'video' | 'audio'; url: string };

type Props = {
  items: SpeakerMediaItem[];
  /** Keeps the stage at the full width of its container instead of shrinking it to leave room
   * for content below it on short screens. */
  fillWidth?: boolean;
  onMediaPlayed?: (item: SpeakerMediaItem, index: number) => void;
};

/** Native speakers saying the target word, each on their own slide: those
 * filmed get a video, those only recorded get a player for their clip. */
export const SpeakerCarousel: FC<Props> = ({ items, fillWidth = false, onMediaPlayed }) => {
  const intl = useIntl();
  const [activeIndex, setActiveIndex] = useState(0);
  const activeItem = items[activeIndex];
  const hasMultipleSpeakers = items.length > 1;
  const handleMediaPlayed = () => onMediaPlayed?.(activeItem, activeIndex);

  return (
    <S.Panel>
      <S.Stage $fillWidth={fillWidth}>
        {activeItem.type === 'video' ? (
          <VideoPlayer
            key={activeItem.url}
            src={activeItem.url}
            playOnKeyboardShortcut={false}
            autoPlay={false}
            onPlay={handleMediaPlayed}
          />
        ) : (
          <S.AudioStage>
            <S.AudioPlayerWrapper>
              <AudioPlayer
                key={activeItem.url}
                src={activeItem.url}
                larger
                onClick={handleMediaPlayed}
              />
            </S.AudioPlayerWrapper>
          </S.AudioStage>
        )}
        {hasMultipleSpeakers && (
          <S.NextSpeakerButton
            type="button"
            onClick={() => setActiveIndex(index => (index + 1) % items.length)}
            aria-label={intl.formatMessage(msgs.nextSpeaker)}
          >
            <Icon name="forward" />
          </S.NextSpeakerButton>
        )}
      </S.Stage>
      {hasMultipleSpeakers && (
        <S.Dots>
          {items.map((item, index) => (
            <S.Dot
              key={item.url}
              type="button"
              $isActive={index === activeIndex}
              onClick={() => setActiveIndex(index)}
              aria-label={intl.formatMessage(msgs.selectSpeaker, { number: index + 1 })}
              aria-current={index === activeIndex}
            />
          ))}
        </S.Dots>
      )}
    </S.Panel>
  );
};
